import React from 'react';
import { LandingPageConfig } from 'src/generated/landing-page-config-schemas/v1/type';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Image from 'next/image';

type Props = Extract<
  LandingPageConfig['data']['slices'][number],
  { type: 'plans' }
> & {
  backgroundColor: string;
};

interface PlanCardProps {
  title: string;
  description: string;
  mealQuantity: number;
  ctaButton: {
    label: string;
    variant: 'contained' | 'outlined' | 'text';
  };
  planLogicalId: string;
  image?: { url?: string };
  singlePlan?: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({
  title,
  description,
  mealQuantity,
  ctaButton,
  planLogicalId,
  image,
  singlePlan,
}) => {
  const theme = useTheme();

  // This is a low-lift solution to support rendering a personalized plan on the landing page.
  // The `PERSONALIZED` logical id is not representative of an actual `cart_order.plans` record in the database.
  const ctaHref =
    planLogicalId === 'PERSONALIZED'
      ? '/personalized-plan'
      : `/menu?plan=${planLogicalId}`;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: theme.palette.primary.main,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        minHeight: singlePlan ? undefined : image?.url ? 320 : 220,
        overflow: 'hidden',
        ...(singlePlan && {
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'center' },
        }),
      }}
    >
      {image?.url && !singlePlan && (
        <Box
          sx={{
            position: 'relative',
            aspectRatio: '21 / 9',
            width: '100%',
          }}
        >
          <Image
            src={image.url}
            alt={title}
            fill
            sizes="100vw"
            style={{ objectFit: 'cover' }}
          />
        </Box>
      )}
      {image?.url && singlePlan && (
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', md: 280 },
            height: { xs: 160, md: 'auto' },
            minHeight: { md: 200 },
            flexShrink: 0,
            alignSelf: { md: 'stretch' },
          }}
        >
          <Image
            src={image.url}
            alt={title}
            fill
            sizes="(max-width: 900px) 100vw, 280px"
            style={{ objectFit: 'cover' }}
          />
        </Box>
      )}
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: singlePlan ? { xs: 1.5, md: 2 } : 1.5,
          p: singlePlan ? { xs: 2.5, md: 4 } : 2.5,
          pb: singlePlan ? undefined : '20px !important',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: singlePlan ? { xs: 17, md: 28 } : { xs: 17, md: 20 },
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.5,
            fontSize: singlePlan ? { xs: 14, md: 18 } : { xs: 14, md: 16 },
            whiteSpace: 'pre-line',
          }}
        >
          {description}
        </Typography>
        {mealQuantity > 0 ? (
          <Chip
            label={`${mealQuantity} meals`}
            size={'medium'}
            sx={{
              alignSelf: 'flex-start',
              mt: 'auto',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 500,
              fontSize: { xs: 12, md: 14 },
              height: singlePlan ? { xs: 'auto', md: 32 } : 'auto',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
            icon={
              <Box
                component="span"
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  ml: '8px !important',
                  flexShrink: 0,
                }}
              />
            }
          />
        ) : null}
      </CardContent>

      <Box
        sx={{
          px: singlePlan ? { xs: 2, md: 4 } : 2,
          pb: singlePlan ? { xs: 2, md: 4 } : 2,
          flexShrink: singlePlan ? 0 : undefined,
        }}
      >
        <Button
          variant={ctaButton.variant}
          href={ctaHref}
          sx={{
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: singlePlan ? { xs: 14, md: 16 } : 14,
            py: singlePlan ? { xs: 1, md: 1.5 } : 1,
            px: singlePlan ? { xs: 2, md: 3 } : 2,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          {ctaButton.label} →
        </Button>
      </Box>
    </Card>
  );
};

const Plans: React.FC<Props> = (props) => {
  const { backgroundColor, title, subtitle, plans } = props;
  const theme = useTheme();

  return (
    <Box sx={{ backgroundColor, py: { xs: 4, md: 10 } }}>
      <Container maxWidth={false} sx={{ maxWidth: '1312px', px: 2 }}>
        {/* Header */}
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '32px', md: '48px' },
            fontWeight: 600,
            textAlign: { xs: 'left', md: 'center' },
            mb: 2,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            textAlign: { xs: 'left', md: 'center' },
            mb: 6,
            fontSize: { xs: '16px', md: '18px' },
            whiteSpace: 'pre-line',
          }}
        >
          {subtitle}
        </Typography>

        {/* Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(auto-fit, minmax(300px, 1fr))',
            },
            gap: { xs: 2, md: 3 },
          }}
        >
          {plans.map((plan) => (
            <PlanCard
              key={plan.title}
              {...plan}
              singlePlan={plans.length === 1}
            />
          ))}
        </Box>

        {/* Footer link */}
        <Typography
          variant="body2"
          align="center"
          sx={{
            color: 'text.secondary',
            mt: 3,
          }}
        >
          Prefer to choose your own meals?{' '}
          <Box
            component="a"
            href="/menu"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            View the full menu
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </Box>
        </Typography>
      </Container>
    </Box>
  );
};

export default Plans;
